import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import Pusher from "pusher-js";
import React, { useEffect, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateStatusForTimeline } from "../../../api/timelineService";
import { addDirector, getAllDirectors } from "../../../api/userService";
import useCustomToast from "../../../customHooks/useCustomToast";
import { setAllDirectors, setNewDirector } from "../../../Redux/companySlice";
import {
  setPreviousWindowRoute,
  setUserTimelineStep,
} from "../../../Redux/userSlice";
import {
  isValidEmail,
  isValidPanCard,
  uploadDirectorPan,
} from "../../../utils/UTILITY_METHODS";
import DotTypingAnimation from "../../PrimaryComponents/DotTypingAnimation";

const DirectorInformation = () => {
  const { phone } = useSelector((store) => store.userDetails);
  const { companyName, directorList } = useSelector(
    (store) => store.companyDetails
  );

  const [directorData, setDirectorData] = useState({
    directorName: "",
    directorPan: "",
    ownershipPercentage: "",
    directorEmail: "",
    directorPhone: phone,
  });

  const [dotTypingAnimation, setDotTypingAnimation] = useState({
    text: "Upload",
    isAnimationRequired: false,
  });

  const dispatch = useDispatch();
  const showToast = useCustomToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDirectors = async () => {
      try {
        const response = await getAllDirectors();
        dispatch(setAllDirectors(response.data));
      } catch (error) {
        showToast(error.message, "error");
      }
    };

    fetchDirectors();
  }, [showToast, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDirectorData((prev) => ({
      ...prev,
      [name]: name === "directorPan" ? value?.toUpperCase() : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const {
      directorName,
      directorPan,
      ownershipPercentage,
      directorEmail,
      directorPhone,
    } = directorData;

    if (
      !directorName?.trim() ||
      !directorPan?.trim() ||
      !ownershipPercentage?.trim() ||
      !directorEmail?.trim() ||
      !directorPhone?.trim()
    ) {
      showToast("All fields are required", "error");
      return;
    }

    if (!isValidPanCard(directorPan)) {
      showToast("Please enter valid PAN number", "error");
      return;
    }

    if (!isValidEmail(directorEmail)) {
      showToast("Please enter valid email id", "error");
      return;
    }

    if (directorPhone?.length !== 10) {
      showToast("Please enter valid phone number", "error");
      return;
    }
    try {
      const response = await addDirector(
        directorName,
        directorPan,
        ownershipPercentage,
        directorEmail,
        directorPhone,
        companyName
      );

      dispatch(setNewDirector(response.data));
      showToast(response.message, "success");
      setDirectorData({
        directorName: "",
        directorPan: "",
        ownershipPercentage: "",
        directorEmail: "",
        directorPhone: phone,
      });
    } catch (error) {
      showToast(error.message, "error");
    }
  };

  const handleKeyDown = (e, nextInputName) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextInputName) {
        const nextInput = document.querySelector(
          `input[name="${nextInputName}"]`
        );
        if (nextInput) {
          nextInput.focus();
        }
      } else {
        handleSubmit(e);
      }
    }
  };

  const handleNextButtonClick = async () => {
    if (!Boolean(directorList.length)) {
      showToast("Please add a Director", "error");
      return;
    }
    dispatch(setPreviousWindowRoute("user"));
    //update status timeline
    await updateStatusForTimeline(
      4,
      "director_information",
      "director_details",
      "completed"
    );
    navigate("/documents");
  };

  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Handle the file upload logic here
      try {
        setDotTypingAnimation({
          text: "Uploading file",
          isAnimationRequired: true,
        });
        const response = await uploadDirectorPan(selectedFile);

        setDirectorData((prev) => {
          return {
            ...prev,
          };
        });

        if (response.status === "passed") {
          setDotTypingAnimation({
            text: "Extracting Details",
            isAnimationRequired: true,
          });
        } else {
          setDotTypingAnimation({
            text: "Upload",
            isAnimationRequired: false,
          });
        }
      } catch (error) {
        showToast(error.message, "error");
        setDotTypingAnimation({
          text: "Upload",
          isAnimationRequired: false,
        });
      }

      event.target.value = null;
    }
  };

  useEffect(() => {
    // Enable Pusher logging - disable this in production
    // Pusher.logToConsole = true;

    // Initialize Pusher
    const pusher = new Pusher("9867b5a5cb231094924f", {
      cluster: "ap2",
    });
    const channel_name = "extract_details_91" + phone;
    const channel = pusher.subscribe(channel_name);

    channel.bind("get-director-details", (payload) => {
      const { director_name, pan_number, status } = payload;
      const new_director_data = {
        directorName: director_name,
        directorPan: pan_number,
      };
      let toast_message = "File processing failed. Please retry again.";
      let toast_nature = "error";

      if (status === "passed") {
        toast_message = "Director Pan details extracted successfully";
        toast_nature = "success";
        showToast(toast_message, toast_nature);
      } else if (status === "blured") {
        toast_message =
          "The PAN card is blurry or invalid. Please upload a clear and valid document.";
        toast_nature = "error";
        showToast(toast_message, toast_nature);
      } else if (status === "failed") {
        showToast(toast_message, toast_nature);
      }

      setDirectorData((prev) => {
        return {
          ...prev,
          ...new_director_data,
        };
      });

      setDotTypingAnimation({
        text: "Upload",
        isAnimationRequired: false,
      });
    });
  }, [phone, showToast]);

  return (
    <Box>
      <Heading as={"h2"} fontSize={"22px"} lineHeight={"22px"}>
        Director Information
      </Heading>

      <form onSubmit={handleSubmit}>
        <HStack spacing={4} my={6}>
          <FormControl>
            <FormLabel
              fontSize={"14px"}
              fontWeight={400}
              htmlFor="directorName"
            >
              Upload Director's Pancard
            </FormLabel>

            <Button
              type="button"
              backgroundColor="orange.400"
              _hover={{ backgroundColor: "orange.500" }}
              color="white"
              size="md"
              fontWeight="bold"
              marginTop={1}
              fontSize="sm"
              width="50%"
              onClick={handleButtonClick}
            >
              <DotTypingAnimation
                text={dotTypingAnimation.text}
                isAnimationRequired={dotTypingAnimation.isAnimationRequired}
              />
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={(e) => handleFileChange(e)}
              accept=".png, .jpg, .jpeg, .pdf" // Restrict to image files with these extensions
            />
          </FormControl>
        </HStack>

        <HStack spacing={4} my={6}>
          <FormControl>
            <FormLabel
              fontSize={"14px"}
              fontWeight={400}
              htmlFor="directorName"
            >
              Director Name
            </FormLabel>
            <Input
              name="directorName"
              id="directorName"
              value={directorData.directorName}
              onChange={handleChange}
              onKeyDown={(e) => handleKeyDown(e, "directorPan")}
              placeholder="Full Name"
              focusBorderColor="teal.500"
              py={"5"}
              className="capitalize"
              fontSize={"14px"}
              backgroundColor={"gray.100"}
              height={"50px"}
              width={"288px"}
              borderRadius={"8px"}
              borderColor={"#E8E8E8"}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize={"14px"} fontWeight={400} htmlFor="directorPan">
              Director PAN
            </FormLabel>
            <Input
              name="directorPan"
              id="directorPan"
              value={directorData.directorPan}
              onChange={handleChange}
              onKeyDown={(e) => handleKeyDown(e, "ownershipPercentage")}
              placeholder="PAN Number"
              focusBorderColor="teal.500"
              py={"5"}
              className="capitalize"
              fontSize={"14px"}
              backgroundColor={"gray.100"}
              height={"50px"}
              width={"288px"}
              borderRadius={"8px"}
              borderColor={"#E8E8E8"}
            />
          </FormControl>
        </HStack>

        <HStack spacing={4} mb={6}>
          <FormControl>
            <FormLabel
              fontSize={"14px"}
              fontWeight={400}
              htmlFor="directorEmail"
            >
              Email
            </FormLabel>
            <Input
              name="directorEmail"
              id="directorEmail"
              type="email"
              value={directorData.directorEmail}
              onChange={handleChange}
              onKeyDown={(e) => handleKeyDown(e, "directorPhone")}
              placeholder="Enter Email Address"
              focusBorderColor="teal.500"
              py={"5"}
              backgroundColor={"white"}
              fontSize={"14px"}
              height={"50px"}
              width={"288px"}
              borderRadius={"8px"}
              borderColor={"#E8E8E8"}
            />
          </FormControl>

          <FormControl>
            <FormLabel
              fontSize={"14px"}
              fontWeight={400}
              htmlFor="directorPhone"
            >
              Phone
            </FormLabel>
            <InputGroup>
              <InputLeftElement
                name="directorPhone"
                pointerEvents="none"
                display={"flex"}
                alignItems={"center"}
                mt={"5px"}
              >
                <ReactCountryFlag
                  countryCode="IN" // Default to India, change as needed
                  svg
                  style={{
                    width: "20px",
                    height: "20px",
                    marginLeft: "2rem",
                  }}
                />
                <Text fontSize={"sm"} marginLeft={"5px"}>
                  +91
                </Text>
              </InputLeftElement>

              <Input
                name="directorPhone"
                id="directorPhone"
                type="tel" // Use 'tel' for better mobile input experience
                value={directorData.directorPhone}
                onChange={handleChange}
                onKeyDown={(e) => handleKeyDown(e, null)}
                placeholder="Enter Phone Number"
                focusBorderColor="teal.500"
                pl="4.1rem"
                py={"5"}
                maxLength={10}
                backgroundColor={"white"}
                fontSize={"14px"}
                height={"50px"}
                width={"288px"}
                borderRadius={"8px"}
                borderColor={"#E8E8E8"}
              />
            </InputGroup>
          </FormControl>
        </HStack>

        <HStack spacing={4} mb={6}>
          <FormControl>
            <FormLabel
              fontSize={"14px"}
              fontWeight={400}
              htmlFor="ownershipPercentage"
            >
              Ownership Percentage
            </FormLabel>
            <Input
              name="ownershipPercentage"
              id="ownershipPercentage"
              type="number"
              value={directorData.ownershipPercentage}
              onChange={handleChange}
              onKeyDown={(e) => handleKeyDown(e, "directorEmail")}
              placeholder="35%"
              focusBorderColor="teal.500"
              py={"5"}
              className="capitalize"
              fontSize={"14px"}
              backgroundColor={"white"}
              height={"50px"}
              width={"288px"}
              borderRadius={"8px"}
              borderColor={"#E8E8E8"}
            />
          </FormControl>
        </HStack>

        <Button
          type="submit"
          backgroundColor={"orange.400"}
          _hover={{ backgroundColor: "orange.500" }}
          color={"white"}
          size={"md"}
          fontWeight={"bold"}
          marginTop={1}
          fontSize={"sm"}
        >
          Add as Director
        </Button>
      </form>

      <Text mt={8} fontSize={"sm"}>
        List of Directors
      </Text>
      <TableContainer
        border={"1px"}
        borderColor={"gray.100"}
        borderRadius={"10px"}
        mt={2}
        width={"full"}
      >
        <Table variant="simple" size={"sm"}>
          <Thead>
            <Tr backgroundColor={"orange.50"}>
              <Th
                fontSize={"14px"}
                fontWeight={"400"}
                textTransform={"capitalize"}
                textAlign="left"
                p={4}
              >
                Name
              </Th>
              <Th
                fontSize={"14px"}
                fontWeight={"400"}
                textTransform={"capitalize"}
                textAlign="left"
                p={4}
              >
                Ownership %
              </Th>
              <Th
                fontSize={"14px"}
                fontWeight={"400"}
                textTransform={"capitalize"}
                textAlign="left"
                p={4}
              >
                Email
              </Th>
              <Th
                fontSize={"14px"}
                fontWeight={"400"}
                textTransform={"capitalize"}
                textAlign="left"
                p={4}
              >
                Phone
              </Th>
            </Tr>
          </Thead>
          <Tbody backgroundColor={"white"}>
            {Boolean(directorList.length) ? (
              <>
                {directorList.map(
                  ({ name, ownership_percentage, email, phone, uuid }) => (
                    <Tr key={uuid}>
                      <Td
                        fontSize={"14px"}
                        fontWeight={"400"}
                        textAlign="left"
                        p={4}
                      >
                        {name}
                      </Td>
                      <Td
                        fontSize={"14px"}
                        fontWeight={"400"}
                        textAlign="left"
                        p={4}
                      >
                        {ownership_percentage}%
                      </Td>
                      <Td
                        fontSize={"14px"}
                        fontWeight={"400"}
                        textAlign="left"
                        p={4}
                      >
                        {email}
                      </Td>
                      <Td
                        fontSize={"14px"}
                        fontWeight={"400"}
                        textAlign="left"
                        p={4}
                      >
                        +91 {phone}
                      </Td>
                    </Tr>
                  )
                )}
              </>
            ) : (
              <Tr>
                <Td colSpan={4} textAlign={"center"} p={4}>
                  No Directors Found
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Box display={"flex"} justifyContent={"flex-start"} columnGap={4} mt={7}>
        <Button
          colorScheme="blackAlpha"
          color={"blackAlpha.700"}
          backgroundColor={"white"}
          size="sm"
          variant="outline"
          width="7.5rem"
          px={8}
          onClick={() => dispatch(setUserTimelineStep(3))}
          _hover={{ backgroundColor: "#eee" }}
        >
          Back
        </Button>
        <Button
          colorScheme="teal"
          size="sm"
          variant="solid"
          px={8}
          onClick={() => handleNextButtonClick()}
          width="7.5rem"
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default DirectorInformation;
